// VENDOR -----------------------------------
// Pins and bundles the libraries that used to be CDN <script> tags. Must be first:
// firebase.initializeApp() below runs at module top level.
import { loadJsPDF, loadBillboard, loadFirestore } from './vendor.js';

// AFTER vendor.js on purpose: vendor imports bootstrap's stylesheet, and these
// are our overrides. Vite emits bundled CSS in import order, so this file has to
// come last or bootstrap wins the cascade.
import './style.css';
import {
  initNotifications,
  enableNotifications,
  disableNotifications,
  permissionState,
  notificationsSupported,
  testNotification,
} from './notifications.js';

// axios was declared here and never called - both axios.get() call sites are
// inside /* */ blocks. The dependency is gone; re-add it if that code is revived.

// FIREBASE ---------------------------------


// SERVICES ---------------------------------
// Extracted in Phase 1. Still published on window because ~9,000 lines below
// reference them as globals, and because index.html templates do too.
import { db, auth } from './services/firebase.js';
import { resetAdminCache } from './services/adminAuth.js';
import { parseLocalDate } from './services/dates.js';
import { RevenueService, RevenueMixin } from './services/revenue.js';

window.parseLocalDate = parseLocalDate;
// Diagnostic, callable from the browser console: await window.testNotification()
window.testNotification = testNotification;
window.RevenueService = RevenueService;
window.RevenueMixin = RevenueMixin;

// COMPONENTS ------------------------------
import { AdminIndicator } from './components/AdminIndicator.js';
import { Auth } from './components/Auth.js';
import { Confirm } from './components/Confirm.js';
import { ProgressBar } from './components/ProgressBar.js';
import { SidebarLayout } from './components/SidebarLayout.js';
import { Admin } from './components/Admin.js';
import { Estimate } from './components/Estimate.js';
import { Expenses } from './components/Expenses.js';
import { Home } from './components/Home.js';
import { JobsMap } from './components/JobsMap.js';
import { InvoiceActivity } from './components/InvoiceActivity.js';
import { Invoices } from './components/Invoices.js';
import { JobScheduler } from './components/JobScheduler.js';
import { MetroInterestCard } from './components/MetroInterestCard.js';
import { ReceiptOCR } from './components/ReceiptOCR.js';
import { SearchTrendsCard } from './components/SearchTrendsCard.js';
import { UserProfile } from './components/UserProfile.js';
import { Terms } from './components/Terms.js';

// PAGE COMPONENTS ---------------------------------










// UI COMPONENTS ---------------------------------







// Enhanced Invoice Activity Component with duplicate prevention

// Invoice Manager Component
// Enhanced Invoice Manager Component with duplicate prevention and performance improvements

// Expense Manager Component  


// ROUTER ---------------------------------

// Add router
const routes = [
  {
    path: "/intake",
    name: "home",
    component: Home,
    meta: { requiresAuth: true },
    props: (route) => ({
      estimateData: route.query.estimateData
    })
  },
  {
    path: "/estimate",
    name: "estimate",
    component: Estimate
  },
  {
    path: "/auth",
    name: "auth",
    component: Auth
  },
  {
    path: "/admin",
    name: "admin",
    component: Admin,
    meta: { requiresAuth: true }
  },
  {
    path: "/terms",
    name: "terms",
    component: Terms
  },
  {
    path: "/confirm",
    name: "confirm",
    component: Confirm,
    meta: { requiresAuth: true }
  },
  {
    path: "/",
    name: "profile",
    component: UserProfile,
    meta: { requiresAuth: true }
  },
  {
    path: "/profile/:id", // Admin views any user's profile
    name: "userProfile",
    component: UserProfile,
    meta: { requiresAuth: true }
  },
  {
    path: "/schedule",
    name: "jobScheduler",
    component: JobScheduler,
    meta: { requiresAuth: true }
  },
  {
    path: "/schedule",
    name: "schedule",
    component: JobScheduler,
    meta: { requiresAuth: true }
  },
  {
    path: "/",
    name: "dashboard",
    component: Home,
    meta: { requiresAuth: true }
  },
  {
    path: "/invoices",
    name: "invoices",
    component: Invoices,
    meta: { requiresAuth: true }
  },
   {
    path: "/expenses",
    name: "Expenses",
    component: Expenses,
    meta: { requiresAuth: true }
   }
];

// Router configuration
const router = VueRouter.createRouter({
  history: VueRouter.createWebHashHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  }
});

// Navigation guard with auth state check
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Create a promise that resolves when auth state is ready
  const getCurrentUser = () => {
    return new Promise((resolve, reject) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      }, reject);
    });
  };

  try {
    const currentUser = await getCurrentUser();

    if (requiresAuth && !currentUser) {
      // Route requires auth but user is not authenticated
      next("/auth");
    } else if (to.path === "/auth" && currentUser) {
      // User is authenticated but trying to access auth page
      next("/");
    } else {
      // Either route doesn't require auth, or user is authenticated
      next();
    }
  } catch (error) {
    console.error("Auth check failed:", error);
    next("/auth");
  }
});

// Initialize app with router
const app = Vue.createApp({
  data() {
    return {
      user: null,
      isAuthenticated: false,
      isLoading: true
    };
  },
  provide() {
    return {
      user: Vue.computed(() => this.user),
      isAuthenticated: Vue.computed(() => this.isAuthenticated),
      handleLogout: this.handleLogout,
      isLoading: Vue.computed(() => this.isLoading)
    };
  },
  methods: {
    async handleLogout() {
      this.isLoading = true;
      try {
        // Otherwise the next user to sign in on this device inherits the
        // previous user's cached admin answer.
        resetAdminCache();
        await auth.signOut();
        this.$router.push("/auth");
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        this.isLoading = false;
      }
    }
  },
  created() {
    this.isLoading = true;
    auth.onAuthStateChanged((user) => {
      this.user = user;
      this.isAuthenticated = !!user;
      this.isLoading = false;
      // Refreshes the push token for a user who already granted permission.
      // No-ops (and imports nothing) for everyone else, so a first-time visitor
      // is never prompted.
      if (user) initNotifications();
    });
  }
});

// REGISTRATION ---------------------------------

app.component("UserProfile", UserProfile);
app.component("AdminIndicator", AdminIndicator);
app.component("JobsMap", JobsMap);
app.component("ProgressBar", ProgressBar);
app.component("job-scheduler", JobScheduler);
app.component("SidebarLayout", SidebarLayout);
app.component("SearchTrendsCard", SearchTrendsCard);
app.component("MetroInterestCard", MetroInterestCard);
app.component("InvoiceActivity", InvoiceActivity);
app.component("ReceiptOCR", ReceiptOCR);


app.use(router);
app.mount("#app");
